import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiRequest, getBackendContext } from "./lib/backendClient";
import { sampleProject } from "./sampleProject";
import type {
  DataField,
  DataTable,
  PageComponent,
  ProjectActivity,
  StudioProject,
  StudioView,
  StudioWorkflow,
  ThemeTokens,
  WorkflowAction,
} from "./types";

interface StudioContextValue {
  project: StudioProject;
  view: StudioView;
  setView: (view: StudioView) => void;
  selectedComponentId: string | null;
  setSelectedComponentId: (id: string | null) => void;
  updateProject: (changes: Partial<StudioProject>) => void;
  updateTheme: (changes: Partial<ThemeTokens>) => void;
  addComponent: (component: PageComponent) => void;
  updateComponent: (id: string, changes: Partial<PageComponent>) => void;
  removeComponent: (id: string) => void;
  moveComponent: (id: string, direction: -1 | 1) => void;
  addTable: (table: DataTable) => void;
  updateTable: (id: string, changes: Partial<DataTable>) => void;
  removeTable: (id: string) => void;
  addField: (tableId: string, field: DataField) => void;
  updateField: (
    tableId: string,
    fieldId: string,
    changes: Partial<DataField>,
  ) => void;
  removeField: (tableId: string, fieldId: string) => void;
  addWorkflow: (workflow: StudioWorkflow) => void;
  updateWorkflow: (id: string, changes: Partial<StudioWorkflow>) => void;
  addWorkflowAction: (workflowId: string, action: WorkflowAction) => void;
  updateWorkflowAction: (
    workflowId: string,
    actionId: string,
    changes: Partial<WorkflowAction>,
  ) => void;
  removeWorkflowAction: (workflowId: string, actionId: string) => void;
  logActivity: (
    action: string,
    detail: string,
    status?: ProjectActivity["status"],
  ) => void;
  resetProject: () => void;
  loadProject: (nextProject: StudioProject) => void;
  syncStatus: "idle" | "saving" | "saved" | "error";
  cloudStatus: "loading" | "ready" | "error";
}

const StudioContext = createContext<StudioContextValue | null>(null);

function loadProject() {
  // El proyecto cloud es la fuente de verdad. La muestra sólo ocupa la memoria de la interfaz
  // hasta que la sesión recupera el proyecto del servidor; nunca se persiste en el dispositivo.
  return sampleProject;
}

export function StudioProvider({ children }: { children: ReactNode }) {
  const [project, setProject] = useState<StudioProject>(loadProject);
  const [view, setView] = useState<StudioView>("home");
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(
    project.components[1]?.id || project.components[0]?.id || null,
  );

  const [syncStatus, setSyncStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [cloudStatus, setCloudStatus] = useState<"loading" | "ready" | "error">("loading");

  // Al montar, si hay una sesión de backend activa, intenta traer la última versión guardada del proyecto.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const ctx = await getBackendContext();
        if (!ctx.baseUrl || !ctx.token) { if (active) setCloudStatus("ready"); return; }
        const { project: remote } = await apiRequest<{ project: StudioProject | null }>(ctx, "/api/project");
        if (active && remote) setProject(remote);
        if (active) setCloudStatus("ready");
      } catch {
        // No se habilita la edición ni el autoguardado: una falla temporal nunca debe
        // reemplazar silenciosamente el proyecto cloud con la plantilla local.
        if (active) setCloudStatus("error");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Autoguardado en la cuenta cloud cuando hay sesión activa, con debounce.
  useEffect(() => {
    if (cloudStatus !== "ready") return;
    const timer = setTimeout(() => {
      (async () => {
        try {
          const ctx = await getBackendContext();
          if (!ctx.baseUrl || !ctx.token) return;
          setSyncStatus("saving");
          await apiRequest(ctx, "/api/project", { method: "PUT", body: JSON.stringify({ project }) });
          setSyncStatus("saved");
        } catch {
          setSyncStatus("error");
        }
      })();
    }, 1200);
    return () => clearTimeout(timer);
  }, [cloudStatus, project]);

  const mutateProject = useCallback(
    (updater: (current: StudioProject) => StudioProject) => {
      setProject((current) => ({
        ...updater(current),
        updatedAt: new Date().toISOString(),
      }));
    },
    [],
  );

  const logActivity = useCallback(
    (
      action: string,
      detail: string,
      status: ProjectActivity["status"] = "info",
    ) => {
      mutateProject((current) => ({
        ...current,
        activity: [
          {
            id: crypto.randomUUID(),
            action,
            detail,
            timestamp: new Date().toISOString(),
            status,
          },
          ...current.activity,
        ].slice(0, 30),
      }));
    },
    [mutateProject],
  );

  const value = useMemo<StudioContextValue>(
    () => ({
      project,
      view,
      setView,
      selectedComponentId,
      setSelectedComponentId,
      updateProject: (changes) =>
        mutateProject((current) => ({ ...current, ...changes })),
      updateTheme: (changes) =>
        mutateProject((current) => ({
          ...current,
          theme: { ...current.theme, ...changes },
        })),
      addComponent: (component) => {
        mutateProject((current) => ({
          ...current,
          components: [...current.components, component],
        }));
        setSelectedComponentId(component.id);
      },
      updateComponent: (id, changes) =>
        mutateProject((current) => ({
          ...current,
          components: current.components.map((component) =>
            component.id === id ? { ...component, ...changes } : component,
          ),
        })),
      removeComponent: (id) => {
        mutateProject((current) => ({
          ...current,
          components: current.components.filter(
            (component) => component.id !== id,
          ),
        }));
        setSelectedComponentId(null);
      },
      moveComponent: (id, direction) =>
        mutateProject((current) => {
          const index = current.components.findIndex(
            (component) => component.id === id,
          );
          const nextIndex = index + direction;
          if (
            index < 0 ||
            nextIndex < 0 ||
            nextIndex >= current.components.length
          ) {
            return current;
          }
          const components = [...current.components];
          [components[index], components[nextIndex]] = [
            components[nextIndex],
            components[index],
          ];
          return { ...current, components };
        }),
      addTable: (table) =>
        mutateProject((current) => ({
          ...current,
          tables: [...current.tables, table],
        })),
      updateTable: (id, changes) =>
        mutateProject((current) => ({
          ...current,
          tables: current.tables.map((table) =>
            table.id === id ? { ...table, ...changes } : table,
          ),
        })),
      removeTable: (id) =>
        mutateProject((current) => ({
          ...current,
          tables: current.tables.filter((table) => table.id !== id),
        })),
      addField: (tableId, field) =>
        mutateProject((current) => ({
          ...current,
          tables: current.tables.map((table) =>
            table.id === tableId
              ? { ...table, fields: [...table.fields, field] }
              : table,
          ),
        })),
      updateField: (tableId, fieldId, changes) =>
        mutateProject((current) => ({
          ...current,
          tables: current.tables.map((table) =>
            table.id === tableId
              ? {
                  ...table,
                  fields: table.fields.map((field) =>
                    field.id === fieldId ? { ...field, ...changes } : field,
                  ),
                }
              : table,
          ),
        })),
      removeField: (tableId, fieldId) =>
        mutateProject((current) => ({
          ...current,
          tables: current.tables.map((table) =>
            table.id === tableId
              ? {
                  ...table,
                  fields: table.fields.filter(
                    (field) => field.id !== fieldId,
                  ),
                }
              : table,
          ),
        })),
      addWorkflow: (workflow) =>
        mutateProject((current) => ({
          ...current,
          workflows: [...current.workflows, workflow],
        })),
      updateWorkflow: (id, changes) =>
        mutateProject((current) => ({
          ...current,
          workflows: current.workflows.map((workflow) =>
            workflow.id === id ? { ...workflow, ...changes } : workflow,
          ),
        })),
      addWorkflowAction: (workflowId, action) =>
        mutateProject((current) => ({
          ...current,
          workflows: current.workflows.map((workflow) =>
            workflow.id === workflowId
              ? { ...workflow, actions: [...workflow.actions, action] }
              : workflow,
          ),
        })),
      updateWorkflowAction: (workflowId, actionId, changes) =>
        mutateProject((current) => ({
          ...current,
          workflows: current.workflows.map((workflow) =>
            workflow.id === workflowId
              ? {
                  ...workflow,
                  actions: workflow.actions.map((action) =>
                    action.id === actionId
                      ? { ...action, ...changes }
                      : action,
                  ),
                }
              : workflow,
          ),
        })),
      removeWorkflowAction: (workflowId, actionId) =>
        mutateProject((current) => ({
          ...current,
          workflows: current.workflows.map((workflow) =>
            workflow.id === workflowId
              ? {
                  ...workflow,
                  actions: workflow.actions.filter(
                    (action) => action.id !== actionId,
                  ),
                }
              : workflow,
          ),
        })),
      logActivity,
      resetProject: () => {
        setProject({
          ...sampleProject,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        setSelectedComponentId(sampleProject.components[1]?.id || null);
      },
      loadProject: (nextProject) => {
        setProject({
          ...nextProject,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        setSelectedComponentId(nextProject.components[1]?.id || nextProject.components[0]?.id || null);
      },
      syncStatus,
      cloudStatus,
    }),
    [
      logActivity,
      mutateProject,
      project,
      selectedComponentId,
      syncStatus,
      cloudStatus,
      view,
    ],
  );

  return (
    <StudioContext.Provider value={value}>{children}</StudioContext.Provider>
  );
}

export function useStudio() {
  const context = useContext(StudioContext);
  if (!context) {
    throw new Error("useStudio debe usarse dentro de StudioProvider");
  }
  return context;
}
